import { useCallback, useEffect, useSyncExternalStore } from "react";
import StorageService from "../services/StorageService";
import { fetchMarketplaceCartProductIds } from "../services/marketplaceCart.service";

let cartProductIds = new Set();
const listeners = new Set();
let fetchPromise = null;
let cartUpdatedListenerAttached = false;

function attachCartUpdatedListener() {
  if (typeof window === "undefined" || cartUpdatedListenerAttached) return;
  cartUpdatedListenerAttached = true;
  window.addEventListener("marketplace-cart-updated", () => {
    void refreshMarketplaceCartProductIds();
  });
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return cartProductIds;
}

function emit(nextIds) {
  cartProductIds = new Set((nextIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0));
  listeners.forEach((listener) => listener());
}

export function refreshMarketplaceCartProductIds() {
  if (!StorageService.isAuthenticated()) {
    emit([]);
    return Promise.resolve([]);
  }

  if (!fetchPromise) {
    fetchPromise = fetchMarketplaceCartProductIds()
      .then((ids) => {
        emit(ids);
        return ids;
      })
      .catch(() => {
        emit([]);
        return [];
      })
      .finally(() => {
        fetchPromise = null;
      });
  }

  return fetchPromise;
}

export function markMarketplaceCartProductAdded(productId) {
  const id = Number(productId);
  if (!Number.isFinite(id) || id <= 0) return;
  emit([...cartProductIds, id]);
}

export default function useMarketplaceCartProductIds() {
  const productIds = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    attachCartUpdatedListener();
    void refreshMarketplaceCartProductIds();
  }, []);

  const isInCart = useCallback(
    (productId) => productIds.has(Number(productId)),
    [productIds],
  );

  return { productIds, isInCart };
}
